import React from 'react';
import { useQuery } from '@tanstack/react-query';
import TopCategoryCard from './TopCategoryCard';
import { categoryColor } from '../../lib/entityColors';
import { fetchExploreCategories } from '../../lib/exploreApi';
import type {
  TopReferencedResponse,
  ExploreSearchResult,
  ExploreCategoriesResponse,
  ExploreCategoryGroup,
} from '../../lib/exploreApi';

interface TopDashboardProps {
  data: TopReferencedResponse | undefined;
  isLoading: boolean;
  onItemClick: (value: string, category: string) => void;
  onCategorySelect?: (category: string) => void;
  category: string;
  browseItems?: ExploreSearchResult[];
  browseTotal?: number;
  browseLoading?: boolean;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
}

function formatNumber(n: number | undefined): string {
  if (n === undefined || n === null) return '0';
  return n.toLocaleString();
}

// Legacy intel stats — these come from the top-referenced "stats" object
// (CVE / AttackTechnique / ThreatActor / MalwareFamily are not in the IOC registry).
const INTEL_STATS = [
  { key: 'total_cves',       label: 'CVEs',      cat: 'cve' },
  { key: 'total_techniques', label: 'TTPs',      cat: 'technique' },
  { key: 'total_actors',     label: 'Actors',    cat: 'actor' },
  { key: 'total_families',   label: 'Families',  cat: 'family' },
  { key: 'total_countries',  label: 'Countries', cat: 'country' },
];
const INTEL_CATS = new Set(INTEL_STATS.map(s => s.cat));

function activeGroupKey(category: string, groups: ExploreCategoryGroup[]): string {
  if (!category) return '';
  if (category.startsWith('group:')) return category.slice('group:'.length);
  const grp = groups.find(g => g.categories.some(c => c.key === category));
  return grp?.key ?? '';
}

function GroupStatRow({
  group,
  category,
  activeGroup,
  onCategorySelect,
}: {
  group: ExploreCategoryGroup;
  category: string;
  activeGroup: string;
  onCategorySelect?: (category: string) => void;
}) {
  // Only show stats for visible (non-empty) sub-categories.
  if (group.categories.length === 0) return null;
  return (
    <div className="explore-stats-row">
      <span className="explore-stats-row-label">{group.label}</span>
      <div className="explore-stats-row-items">
        {group.categories.map((c) => {
          let cls = 'explore-stat';
          if (category) {
            const matches = c.key === category || group.key === activeGroup;
            cls += matches ? ' explore-stat--active' : ' explore-stat--muted';
          }
          if (onCategorySelect) cls += ' explore-stat--clickable';
          return (
            <button
              key={c.key}
              type="button"
              className={cls}
              onClick={() => onCategorySelect?.(c.key)}
              disabled={!onCategorySelect}
              title={`Filter by ${c.label}`}
            >
              <span className="explore-stat-value" style={{ color: categoryColor(c.key) }}>
                {formatNumber(c.count)}
              </span>
              <span className="explore-stat-label">{c.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function IntelStatRow({
  stats,
  category,
  onCategorySelect,
}: {
  stats: Record<string, number>;
  category: string;
  onCategorySelect?: (category: string) => void;
}) {
  const visible = INTEL_STATS.filter(({ key }) => {
    const val = stats[key];
    return val !== undefined && val !== 0;
  });
  if (visible.length === 0) return null;

  return (
    <div className="explore-stats-row">
      <span className="explore-stats-row-label">Intel</span>
      <div className="explore-stats-row-items">
        {visible.map(({ key, label, cat }) => {
          let cls = 'explore-stat';
          if (category) {
            cls += cat === category ? ' explore-stat--active' : ' explore-stat--muted';
          }
          if (onCategorySelect) cls += ' explore-stat--clickable';
          const count = stats[key];
          return (
            <button
              key={key}
              type="button"
              className={cls}
              onClick={() => onCategorySelect?.(cat)}
              disabled={!onCategorySelect}
              title={`${label}: ${count} entity ${count === 1 ? 'node' : 'nodes'} in the graph`}
            >
              <span className="explore-stat-value" style={{ color: categoryColor(cat) }}>
                {formatNumber(count)}
              </span>
              <span className="explore-stat-label">{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function TopDashboard({
  data, isLoading, onItemClick, onCategorySelect, category,
  browseItems, browseTotal, browseLoading, page, pageSize, onPageChange,
}: TopDashboardProps) {
  const { data: catsData } = useQuery<ExploreCategoriesResponse>({
    queryKey: ['explore-categories'],
    queryFn: fetchExploreCategories,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="explore-dashboard">
        <div className="explore-stats-banner explore-stats-banner--loading">
          <div className="explore-stats-skeleton" />
        </div>
        <div className="explore-top-grid">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="explore-top-card explore-top-card--loading">
              <div className="explore-skeleton-title" />
              <div className="explore-skeleton-list">
                {Array.from({ length: 5 }).map((_, j) => (
                  <div key={j} className="explore-skeleton-row" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const stats = data.stats || {};
  const groups = catsData?.groups ?? [];
  const activeGroup = activeGroupKey(category, groups);

  // Filter categories shown in the top-referenced grid.
  // - When a specific sub-category is selected: show only that.
  // - When a group is selected: show all of its sub-categories.
  // - When no category: show every category we have data for.
  const filteredCategories = (() => {
    if (!category) return data.categories;
    if (category.startsWith('group:')) {
      const grp = groups.find(g => g.key === activeGroup);
      const keys = new Set(grp?.categories.map(c => c.key) ?? []);
      return data.categories.filter(c => keys.has(c.category));
    }
    return data.categories.filter(c => c.category === category);
  })();
  const isSingle = filteredCategories.length === 1;

  const showRegistryStats = !category || activeGroup !== '';
  const showIntelStats = !category || INTEL_CATS.has(category);

  return (
    <div className="explore-dashboard">
      <div className="explore-stats-banner">
        <div className="explore-stats-doc-count">
          <span className="explore-stat-value">{formatNumber(stats.total_documents)}</span>
          <span className="explore-stat-label">Documents</span>
        </div>
        {showRegistryStats && groups.map((grp) => {
          // Hide other groups when a non-matching sub-category is selected.
          if (category && !category.startsWith('group:')
              && activeGroup !== grp.key && !INTEL_CATS.has(category)) {
            return null;
          }
          return (
            <GroupStatRow
              key={grp.key}
              group={grp}
              category={category}
              activeGroup={activeGroup}
              onCategorySelect={onCategorySelect}
            />
          );
        })}
        {showIntelStats && (
          <IntelStatRow stats={stats} category={category} onCategorySelect={onCategorySelect} />
        )}
      </div>

      {category && (
        <div className={`explore-top-grid${isSingle ? ' explore-top-grid--single' : ''}`}>
          {filteredCategories.map((cat) => {
            const useBrowse = isSingle && browseItems !== undefined;
            return (
              <TopCategoryCard
                key={cat.category}
                category={cat.category}
                items={useBrowse ? browseItems : cat.items}
                onItemClick={onItemClick}
                expanded={isSingle}
                isLoading={useBrowse && browseLoading}
                page={useBrowse ? page : undefined}
                pageSize={useBrowse ? pageSize : undefined}
                total={useBrowse ? browseTotal : undefined}
                onPageChange={useBrowse ? onPageChange : undefined}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
